import {
  type AiFeature,
  DEFAULT_AI_FEATURES,
  type Workspace,
} from "@/types/workspace";

// Storage key constant
export const PREFERRED_WORKSPACE_ID_KEY = "preferredWorkspaceId";

// Get saved workspace preference
export const getPreferredWorkspaceId = (): string | null => {
  try {
    return localStorage.getItem(PREFERRED_WORKSPACE_ID_KEY);
  } catch (error) {
    console.error("Error reading workspace preference:", error);

    return null;
  }
};

// Save workspace preference
export const setPreferredWorkspaceId = (workspaceId: string): void => {
  try {
    localStorage.setItem(PREFERRED_WORKSPACE_ID_KEY, workspaceId);
  } catch (error) {
    console.error("Error saving workspace preference:", error);
  }
};

// Clear workspace preference
export const clearPreferredWorkspaceId = (): void => {
  try {
    localStorage.removeItem(PREFERRED_WORKSPACE_ID_KEY);
  } catch (error) {
    console.error("Error clearing workspace preference:", error);
  }
};

// Get workspace to activate (checks localStorage, validates against available workspaces)
export const getWorkspaceToActivate = (
  workspaces: Array<{ id: string }>,
): string | null => {
  if (!workspaces || workspaces.length === 0) {
    return null;
  }

  const preferredId = getPreferredWorkspaceId();

  // Validate saved preference against current workspaces
  if (preferredId) {
    const hasAccess = workspaces.some(
      (workspace) => workspace.id === preferredId,
    );

    if (hasAccess) {
      return preferredId;
    }
    // Clear invalid preference (workspace was deleted or user lost access)
    clearPreferredWorkspaceId();
  }

  // Fallback to first workspace
  return workspaces[0].id;
};

/** Whether admin picked `feature` for the workspace, its AI switch aside. */
export const hasAiFeature = (
  workspace: Workspace | null,
  feature: AiFeature,
): boolean => (workspace?.aiFeatures ?? DEFAULT_AI_FEATURES).includes(feature);

/** Picked in admin and the workspace's own AI switch on: the pair the backend
 *  enforces, so a control gated on anything less 403s. */
export const isAiFeatureOn = (
  workspace: Workspace | null,
  feature: AiFeature,
): boolean =>
  Boolean(workspace?.config?.aiEnabled) && hasAiFeature(workspace, feature);
